import { SafetyLocalStorage } from '@mail-core/std';
import { requestPayment } from 'Cloud/Application/Payment';
import { logger } from 'lib/logger';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { IS_IOS_MOBILE } from 'reactApp/appHelpers/configHelpers';
import { WhatsNewDialog } from 'reactApp/components/WhatsNewDialog/WhatsNewDialog';
import { MediaSelectors } from 'reactApp/modules/media/media.selectors';
import { ProductsSelectors } from 'reactApp/modules/products/products.selectors';
import { SUMMER_PROMO_2026_MODAL_QUERY_PARAM } from 'reactApp/modules/promoNew/items/summerCampaign2026ModalCreator';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { checkMobilePayAvailable } from 'reactApp/sections/QuotaLanding/QuotaLanding.helpers';
import { revealPlatformPopup } from 'reactApp/sections/QuotaLanding/TariffsPlans/TariffPlansCard/TariffPlansCard.helpers';
import type { RootState } from 'reactApp/store';
import { LegalBlock } from 'reactApp/ui/SummerCampaign2026Modal/LegalBlock/LegalBlock';
import { SummerCampaign2026ModalMobile } from 'reactApp/ui/SummerCampaign2026Modal/SummerCampaign2026ModalMobile/SummerCampaign2026ModalMobile';
import { showAuth } from 'reactApp/utils/auth';
import { sendDwh, sendXray } from 'reactApp/utils/ga';

import styles from './SummerCampaign2026Modal.css';
import { isTariffDataExpired, SUMMER_PROMO_2026_TARIFF_KEY, tariffConfigs, touchTariffConfig } from './SummerCampaign2026Modal.helpers';
import {
    type StoredTariffData,
    type SummerCampaign2026ModalProps,
    type TariffConfig,
    type TariffKey,
    type TouchTariffConfig,
    ImageStates,
} from './SummerCampaign2026Modal.types';

const MODAL_NAME = 'mnz_summer_promo_2026';
const ANONYMOUS_TARIFF_KEY = `anonymous|${SUMMER_PROMO_2026_TARIFF_KEY}`;

const sendAnalytics = (action: 'show' | 'click' | 'close' | 'click-reject', platform: 'web' | 'touch', buttonName?: string) => {
    sendDwh({
        eventCategory: 'modal',
        action,
        p: 'cloud-web-analytics',
        dwhData: {
            name: MODAL_NAME,
            platform,
            ...(buttonName && { button_name: buttonName }),
        },
    });
};

export const SummerCampaign2026Modal: React.FC<SummerCampaign2026ModalProps> = ({ onClose, hide, complete }) => {
    const isMobile = useSelector(MediaSelectors.isMobile);
    const isAnonymous = useSelector(UserSelectors.isAnonymous);
    const LocalStorage = new SafetyLocalStorage({});
    const email = useSelector(UserSelectors.getEmail);

    const isIOSPromo = window.location.pathname.includes('/promo/quota/ios');
    const isAndroidPromo = window.location.pathname.includes('/promo/quota/android');
    const { isMobilePayAvailable } = checkMobilePayAvailable(isIOSPromo, isAndroidPromo);

    const storageKey = `${email}|${SUMMER_PROMO_2026_TARIFF_KEY}`;

    if (!isAnonymous && email) {
        const anonymousRawData = LocalStorage.getItem(ANONYMOUS_TARIFF_KEY);
        if (anonymousRawData) {
            try {
                const anonymousData = JSON.parse(anonymousRawData) as StoredTariffData;
                if (!isTariffDataExpired(anonymousData)) {
                    LocalStorage.setItem(storageKey, anonymousRawData);
                    LocalStorage.removeItem(ANONYMOUS_TARIFF_KEY);
                }
            } catch (error) {
                logger.error(error);
                sendXray(['summer_promo_2026_migration_error', String(error)]);
            }
        }
    }

    const storedRawData = LocalStorage.getItem(storageKey);
    let storedData: StoredTariffData | null = null;

    try {
        storedData = storedRawData ? (JSON.parse(storedRawData) as StoredTariffData) : null;
    } catch {
        storedData = null;
    }

    const storedTariff = storedData && !isTariffDataExpired(storedData) ? storedData.tariff : null;
    const hasStoredTariff = Boolean(storedTariff);

    const [imageState, setImageState] = useState<ImageStates>(hasStoredTariff ? ImageStates.end : ImageStates.start);
    const [isSellState, setIsSellState] = useState<boolean>(hasStoredTariff);
    const timeoutId = useRef<ReturnType<typeof setTimeout> | null>(null);
    const selectedTariffKey = useRef<TariffKey | null>(storedTariff);

    const config = useMemo((): TariffConfig => {
        if (storedTariff && tariffConfigs[storedTariff]) {
            return tariffConfigs[storedTariff];
        }

        const keys = Object.keys(tariffConfigs) as TariffKey[];
        const randomKey = keys[Math.floor(Math.random() * keys.length)];
        selectedTariffKey.current = randomKey;
        return tariffConfigs[randomKey];
    }, [storedTariff]);

    const touchConfig = useMemo((): TouchTariffConfig => {
        const key = selectedTariffKey.current;
        if (key && touchTariffConfig[key]) {
            return touchTariffConfig[key];
        }
        const keys = Object.keys(touchTariffConfig) as TariffKey[];
        return touchTariffConfig[keys[0]];
    }, []);

    const product = useSelector((state: RootState) => ProductsSelectors.getProductById(state, config.tariffId));
    const spaceValue = product?.space?.value ?? '512 ГБ';

    useEffect(() => {
        return () => {
            if (timeoutId.current) {
                clearTimeout(timeoutId.current);
            }
        };
    }, []);

    // Добавляем query-параметр в URL при открытии модалки,
    // чтобы другие промо-модалки не показывались поверх этой.
    // Убираем параметр при закрытии.
    // useLayoutEffect — синхронно после DOM-мутации, до того как сработает любой setTimeout/таймер 3promo,
    // чтобы исключить гонку между маунтом модалки и проверками в test()-функциях других промок.
    useLayoutEffect(() => {
        const url = new URL(window.location.href);
        if (!url.searchParams.has(SUMMER_PROMO_2026_MODAL_QUERY_PARAM)) {
            url.searchParams.set(SUMMER_PROMO_2026_MODAL_QUERY_PARAM, '1');
            window.history.replaceState(null, '', url.toString());
        }

        return () => {
            const currentUrl = new URL(window.location.href);
            if (currentUrl.searchParams.has(SUMMER_PROMO_2026_MODAL_QUERY_PARAM)) {
                currentUrl.searchParams.delete(SUMMER_PROMO_2026_MODAL_QUERY_PARAM);
                window.history.replaceState(null, '', currentUrl.toString());
            }
        };
    }, []);

    const textConfig = useMemo(() => {
        return {
            sellState: {
                title: 'Подключайте бесплатно и участвуйте в розыгрыше!',
                // eslint-disable-next-line no-irregular-whitespace
                text: `У вас будет бесконечное место для фото с телефона +${spaceValue} для других файлов. Розыгрыш летних призов каждую неделю, а колесо можно перекрутить завтра`,
                primaryButtonText: 'Забрать приз',
                secondaryButtonText: 'Попробовать завтра',
            },
            initState: {
                title: 'Дарим место для летних фото и призы, которые просятся в кадр',
                text: 'Подключите Mail Space, чтобы всегда было место на телефоне. Разыграем призы среди подключивших — пляжные зонты, надувные круги, бассейн и айфон!',
                primaryButtonText: 'Крутить колесо',
                secondaryButtonText: 'Не сейчас',
            },
        };
    }, [spaceValue]);

    const { title, text, primaryButtonText, secondaryButtonText } = textConfig[isSellState ? 'sellState' : 'initState'];

    const platform = isMobile ? 'touch' : 'web';

    const handleShow = useCallback(() => {
        sendAnalytics('show', platform);
    }, [platform]);

    const handlePrimaryClick = useCallback(
        (paySource: string) => {
            sendAnalytics('click', platform, primaryButtonText);

            if (isSellState) {
                if (isAnonymous) {
                    if (selectedTariffKey.current) {
                        LocalStorage.setItem(
                            ANONYMOUS_TARIFF_KEY,
                            JSON.stringify({
                                tariff: selectedTariffKey.current,
                                timestamp: Date.now(),
                            })
                        );
                    }
                    const successPage = new URL(window.location.href);
                    successPage.searchParams.set(SUMMER_PROMO_2026_MODAL_QUERY_PARAM, '1');
                    showAuth({
                        modal: true,
                        successPage: successPage.toString(),
                    });
                    complete?.();
                    onClose?.();
                    return;
                }

                if ((isAndroidPromo || isIOSPromo) && !isMobilePayAvailable) {
                    revealPlatformPopup(config.tariffId, isIOSPromo, isAndroidPromo);
                    complete?.();
                    onClose?.();
                    return;
                }

                requestPayment({
                    tariff: config.tariffId,
                    forceFastCheckout: true,
                    paySource,
                });
                complete?.();
                onClose?.();
                return;
            }

            // На iOS .webm-анимация кручения колеса не проигрывается (ни в вебвью, ни в Safari) —
            // пропускаем её и сразу показываем финальный стейт.
            if (IS_IOS_MOBILE) {
                setIsSellState(true);
                setImageState(ImageStates.end);
                if (selectedTariffKey.current) {
                    try {
                        LocalStorage.setItem(
                            storageKey,
                            JSON.stringify({
                                tariff: selectedTariffKey.current,
                                timestamp: Date.now(),
                            })
                        );
                    } catch (error) {
                        logger.error(error);
                        sendXray('summer_promo_2026_localstorage_error');
                    }
                }
                return;
            }

            setImageState(ImageStates.inProgress);

            if (!timeoutId.current) {
                timeoutId.current = setTimeout(() => {
                    setIsSellState(true);
                    if (selectedTariffKey.current) {
                        LocalStorage.setItem(
                            storageKey,
                            JSON.stringify({
                                tariff: selectedTariffKey.current,
                                timestamp: Date.now(),
                            })
                        );
                    }
                }, 5300);
            }
        },
        [
            isSellState,
            isAnonymous,
            config.tariffId,
            onClose,
            complete,
            storageKey,
            platform,
            primaryButtonText,
            isAndroidPromo,
            isIOSPromo,
            isMobilePayAvailable,
        ]
    );

    const handleSecondaryClick = useCallback(() => {
        sendAnalytics('click-reject', platform, secondaryButtonText);
        hide?.();
        onClose?.();
    }, [onClose, hide, platform, secondaryButtonText]);

    const handleClose = useCallback(() => {
        sendAnalytics('close', platform);
        hide?.();
        onClose?.();
    }, [onClose, hide, platform]);

    if (isMobile) {
        return (
            <SummerCampaign2026ModalMobile
                onClose={handleClose}
                title={title}
                text={text}
                primaryButtonText={primaryButtonText}
                secondaryButtonText={secondaryButtonText}
                imageState={imageState}
                config={touchConfig}
                erid={config.erid}
                onShow={handleShow}
                onPrimaryClick={() => handlePrimaryClick('summer_promo_2026_mobile')}
                onSecondaryClick={handleSecondaryClick}
            />
        );
    }

    return (
        <WhatsNewDialog
            title={title}
            text={text}
            titleClassName={styles.title}
            textClassName={styles.text}
            primaryButtonText={primaryButtonText}
            secondaryButtonText={secondaryButtonText}
            imageUrl=""
            imageOnlyAdditionalContent
            imageWrapperClassName={styles.imageWrapper}
            imageAdditionalContent={
                <>
                    {imageState === ImageStates.inProgress && (
                        <video autoPlay muted playsInline className={styles.animation}>
                            <source src={config.animation} type="video/webm" />
                        </video>
                    )}
                    <img
                        className={styles.image}
                        src={imageState === ImageStates.end ? config.endImg : config.startImg}
                        alt="Летняя акция"
                    />
                    <LegalBlock erid={config.erid} />
                </>
            }
            closable={false}
            closeOnDimmerClick
            onClose={handleClose}
            onShow={handleShow}
            onClick={() => handlePrimaryClick('summer_promo_2026')}
            onSecondary={handleSecondaryClick}
        />
    );
};

export default SummerCampaign2026Modal;
